import {
  useLayoutEffect,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import throttle from 'lodash/throttle';
import { StyledFolders, StyledFoldersScrollWrapper } from './styles';
import { useNavBarState } from 'app/navBarController';
import { FOLDERS_SIDEBAR_ID } from './constants';
import { ScrollTrigger } from 'components/ScrollTrigger';
import { useDragAndDropContext } from '../FoldersDragAndDrop/context';

export const FoldersWrapper: FC<
  PropsWithChildren<{
    scrollContainerRef: React.RefObject<HTMLDivElement>;
  }>
> = ({ children, scrollContainerRef }) => {
  const { height } = useNavBarState();
  const [windowTop, setWindowTop] = useState(0);
  const [scrolledTop, setScrolledTop] = useState(false);
  const [scrolledBottom, setScrolledBottom] = useState(false);
  const { dragging } = useDragAndDropContext();

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;

      const handleScroll = throttle(
        () => {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
          setScrolledTop(scrollTop === 0);
          setScrolledBottom(scrollTop + clientHeight >= scrollHeight - 1);
        },
        100,
        { leading: true, trailing: true }
      );

      const handleWindowScroll = throttle(
        () => {
          setWindowTop(window.scrollY);
        },
        100,
        { leading: true, trailing: true }
      );

      const resizeObserver = new ResizeObserver(handleScroll);

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      window.addEventListener('scroll', handleWindowScroll, {
        passive: true,
      });
      resizeObserver.observe(scrollContainer);

      handleScroll();

      return () => {
        handleScroll.cancel();
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleWindowScroll);
        resizeObserver.disconnect();
      };
    }
  }, [scrollContainerRef]);

  return (
    <StyledFolders
      id={FOLDERS_SIDEBAR_ID}
      navBarHeight={height}
      windowTop={windowTop}
      scrolledTop={scrolledTop}
      scrolledBottom={scrolledBottom}
    >
      <StyledFoldersScrollWrapper
        ref={scrollContainerRef}
        className="kds-expanding-scrollbar"
      >
        {children}
      </StyledFoldersScrollWrapper>
      <ScrollTrigger
        scrollContainerRef={scrollContainerRef}
        enabled={!scrolledTop && dragging}
        position="top"
        size={70}
        offset={-30}
        zIndex={1}
      />
      <ScrollTrigger
        scrollContainerRef={scrollContainerRef}
        enabled={!scrolledBottom && dragging}
        position="bottom"
        size={70}
        offset={-30}
        zIndex={1}
      />
    </StyledFolders>
  );
};
